/**
 * FirmPickCard — la Decisión Firme del usuario en /mi-decision.
 *
 * Muestra la propiedad que el usuario declaró como "mi mejor opción".
 * Cuando la decisión firme es un proyecto de constructora (source==='constructor_project'),
 * mostramos también el nombre del developer (logo textual) y un CTA "Descargar Brochure".
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Award, ExternalLink, Trash2, MessageSquare, Download, FileText } from 'lucide-react';

export default function FirmPickCard({ firmPick, onDelete, className = '' }) {
  if (!firmPick) return null;
  const savedDate = firmPick.saved_at ? new Date(firmPick.saved_at) : null;
  const isConstructorProject = firmPick.source === 'constructor_project';
  const developerInitials = (firmPick.developer_name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'CO';

  return (
    <div
      className={`bg-gradient-to-br from-habita to-habita-dark text-white rounded-2xl p-5 shadow-md ${className}`}
      data-testid="firm-pick-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full bg-white/20 grid place-items-center shrink-0">
          <Award className="w-4 h-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-white/80 font-semibold">
            {isConstructorProject ? 'Mi decisión firme · Proyecto nuevo' : 'Mi decisión firme'}
          </p>
          <h3 className="font-serif text-xl font-bold leading-tight truncate" data-testid="firm-pick-title">
            {isConstructorProject
              ? (firmPick.constructor_project_name || firmPick.property_title || 'Proyecto seleccionado')
              : (firmPick.property_title || 'Propiedad seleccionada')}
          </h3>
        </div>
      </div>

      {/* Developer badge — sólo para vivienda nueva */}
      {isConstructorProject && firmPick.developer_name && (
        <div
          className="flex items-center gap-2.5 bg-white/10 rounded-xl p-2.5 mb-3"
          data-testid="firm-pick-developer"
        >
          <div className="w-10 h-10 rounded-lg bg-white text-habita font-serif font-bold grid place-items-center shrink-0">
            {developerInitials}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-white/60">Constructora</p>
            <p className="text-sm font-semibold truncate">{firmPick.developer_name}</p>
          </div>
          {firmPick.stage && (
            <span className="ml-auto text-[10px] uppercase tracking-widest bg-white/20 rounded-full px-2 py-0.5 shrink-0 capitalize">
              {firmPick.stage}
            </span>
          )}
        </div>
      )}

      {isConstructorProject && firmPick.typology_name && (
        <p className="text-xs text-white/80 mb-3">
          Tipología <strong>{firmPick.typology_name}</strong> · guardada como tu elección
        </p>
      )}

      <div className="flex items-baseline gap-3 mb-4">
        {typeof firmPick.habita_score === 'number' && (
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/60">Habita Score al guardar</p>
            <p className="font-serif text-3xl font-bold" data-testid="firm-pick-score">{firmPick.habita_score}</p>
          </div>
        )}
        {savedDate && (
          <p className="text-[11px] text-white/70 ml-auto">
            Guardada el {savedDate.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' })}
          </p>
        )}
      </div>

      {firmPick.reason && (
        <div className="bg-white/10 rounded-lg p-3 mb-3">
          <p className="text-[10px] uppercase tracking-widest text-white/60 font-semibold mb-1">Por qué la elegí</p>
          <p className="text-sm leading-relaxed">{firmPick.reason}</p>
        </div>
      )}

      {firmPick.notes && (
        <div className="bg-white/10 rounded-lg p-3 mb-4 flex gap-2">
          <MessageSquare className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
          <p className="text-sm leading-relaxed italic">&ldquo;{firmPick.notes}&rdquo;</p>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {/* CTA principal para vivienda nueva: descargar brochure */}
        {isConstructorProject && firmPick.brochure_url ? (
          <a
            href={firmPick.brochure_url}
            target="_blank"
            rel="noopener noreferrer"
            download
            data-testid="firm-pick-brochure-btn"
            className="inline-flex items-center gap-1.5 bg-white text-habita font-semibold text-sm px-4 h-10 rounded-full hover:opacity-90 transition-opacity"
          >
            <Download className="w-4 h-4" />
            Descargar Brochure
          </a>
        ) : firmPick.property_id ? (
          <Link
            to={`/propiedad/${firmPick.property_id}`}
            data-testid="firm-pick-open"
            className="inline-flex items-center gap-1.5 bg-white text-habita font-semibold text-sm px-4 h-10 rounded-full hover:opacity-90 transition-opacity"
          >
            Volver a verla <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        ) : null}

        {/* Lista de precios como CTA secundario (vivienda nueva) */}
        {isConstructorProject && firmPick.pricelist_url && (
          <a
            href={firmPick.pricelist_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="firm-pick-pricelist-btn"
            className="inline-flex items-center gap-1.5 border border-white/40 text-white text-sm px-4 h-10 rounded-full hover:bg-white/10 transition-colors"
          >
            <FileText className="w-4 h-4" />
            Lista de precios
          </a>
        )}

        {onDelete && (
          <button
            onClick={onDelete}
            data-testid="firm-pick-delete"
            className="inline-flex items-center gap-1.5 text-white/90 hover:text-white text-xs px-3 h-10 rounded-full border border-white/30 hover:bg-white/10 transition-colors ml-auto"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Aún estoy explorando — quitar
          </button>
        )}
      </div>
    </div>
  );
}
